import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { sortByStartDate } from 'app/events/helpers/sort-by-start-date';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { add, sub } from 'date-fns';
import { SearchCalendarEventsGQL } from 'generated/graphql';
import { debounceAllButFirst } from 'helpers';
import { takeUntil } from 'rxjs';

export interface SelectEventsDialogData {
  selectedEventIds?: string[];
  dateRange?: Partial<{
    start: Date;
    end: Date;
  }>;
  keyword?: string;
}

@Component({
  selector: 'app-select-events-dialog',
  templateUrl: `./select-events.dialog.html`,
  styleUrls: ['./select-events.dialog.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SelectEventsDialog extends WithDisposableSubject() implements OnInit {
  static open(dialog: MatDialog, config: MatDialogConfig<SelectEventsDialogData>) {
    return dialog.open<SelectEventsDialog, SelectEventsDialogData, SelectEventsDialogData>(SelectEventsDialog, config);
  }

  today = new Date();
  events = [];
  selectedEvents = [];
  searchCtrl = new FormControl<string>(null);
  dateRangeFilter = new FormGroup({
    start: new FormControl<Date>(sub(this.today, { months: 2 })),
    end: new FormControl<Date>(add(this.today, { months: 2 })),
  });
  eventsLoading = true;

  get campaignId() {
    return this._route.snapshot.queryParams['campaignId'] || null;
  }

  constructor(
    public dialogRef: MatDialogRef<SelectEventsDialog, SelectEventsDialogData>,
    private _route: ActivatedRoute,
    private _searchCalendarEventsGQL: SearchCalendarEventsGQL,
    @Inject(MAT_DIALOG_DATA) public data: SelectEventsDialogData
  ) {
    super();
    const { selectedEventIds, dateRange, keyword } = data;
    this.selectedEvents = selectedEventIds || [];
    if (dateRange) {
      this.dateRangeFilter.patchValue(dateRange);
    }
    if (keyword) {
      this.searchCtrl.setValue(keyword);
    }
    // TODO: Handle existing selectedEventIds:
    // We might need to query them separately to get their event info,
    // since it might not be part of the current filters
    this.updateEvents();
  }

  ngOnInit() {
    this._watchSearchInput();
  }

  updateEvents() {
    this.eventsLoading = true;
    const { start, end } = this.dateRangeFilter.value;
    this._searchCalendarEventsGQL
      .fetch(
        {
          campaignId: this.campaignId,
          dateRange: {
            min: start.toISOString(),
            max: end.toISOString(),
          },
          keyword: this.searchCtrl.value,
        },
      )
      .subscribe(({ data }) => {
        this.events = [
          ...data.searchCalendarEvents.nodes,
          // Keep events that were previously selected
          ...this.events.filter(e =>
            this.selectedEvents.includes(e.id) &&
            !data.searchCalendarEvents.nodes.find(e2 => e2.id === e.id)),
        ]
          .sort(sortByStartDate);
        this.eventsLoading = false;
      });
  }

  private _watchSearchInput() {
    this.searchCtrl.valueChanges
      .pipe(
        takeUntil(this.disposer),
        debounceAllButFirst(250)
      )
      .subscribe((search) => {
        this.updateEvents();
      });
  }

  onDateChange() {
    // Called on input or datepicker blur
    const { start, end } = this.dateRangeFilter.value;
    if (!start || !end) {
      return;
    }
    this.updateEvents();
  }

  selectEvents() {
    this.dialogRef.close({
      selectedEventIds: this.selectedEvents,
      dateRange: this.dateRangeFilter.value,
      keyword: this.searchCtrl.value,
    });
  }

  clearEvents() {
    this.selectedEvents = [];
    this.dialogRef.close({
      selectedEventIds: [],
      dateRange: this.dateRangeFilter.value,
      keyword: this.searchCtrl.value,
    });
  }

}
